import { useEffect, type ReactNode } from 'react'
import { Button } from './Button'
import { cx } from './utils'

/** Ίδιο ύφος με το διάλογο στη σελίδα /offer-accept */
export type ConfirmationDialogIntent = 'accept' | 'reject' | 'archive' | 'default'

export type ConfirmationDialogSummary = {
  title: string
  lines?: ReactNode[]
}

export type ConfirmationDialogProps = {
  open: boolean
  title: string
  description?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  intent?: ConfirmationDialogIntent
  summary?: ConfirmationDialogSummary
  busy?: boolean
  onClose: () => void
  onConfirm: () => void
}

const intentPanelClass: Record<
  Exclude<ConfirmationDialogIntent, 'default'>,
  string
> = {
  accept:
    'border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-900/60 dark:bg-emerald-950 dark:text-emerald-50',
  reject:
    'border-red-200 bg-red-50 text-red-950 dark:border-red-900/60 dark:bg-red-950 dark:text-red-50',
  archive:
    'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950 dark:text-amber-50',
}

const intentBadgeClass: Record<
  Exclude<ConfirmationDialogIntent, 'default'>,
  string
> = {
  accept: 'bg-emerald-600',
  reject: 'bg-red-600',
  archive: 'bg-amber-600',
}

const intentConfirmClass: Record<
  Exclude<ConfirmationDialogIntent, 'default'>,
  string
> = {
  accept: 'bg-emerald-600 hover:bg-emerald-500 focus-visible:outline-emerald-500',
  reject: 'bg-red-600 hover:bg-red-500 focus-visible:outline-red-500',
  archive: 'bg-amber-600 hover:bg-amber-500 focus-visible:outline-amber-500',
}

function IntentBadge({
  intent,
}: {
  intent: Exclude<ConfirmationDialogIntent, 'default'>
}) {
  if (intent === 'archive') {
    return (
      <div
        className={cx(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white',
          intentBadgeClass[intent],
        )}
        aria-hidden
      >
        <svg
          className="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 7h16M6 7V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2m2 0v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7"
          />
        </svg>
      </div>
    )
  }
  const glyph = intent === 'accept' ? '✓' : '!'
  return (
    <div
      className={cx(
        'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-lg font-bold text-white',
        intentBadgeClass[intent],
      )}
      aria-hidden
    >
      {glyph}
    </div>
  )
}

export function ConfirmationDialog({
  open,
  title,
  description,
  confirmLabel = 'Επιβεβαίωση',
  cancelLabel = 'Ακύρωση',
  intent = 'default',
  summary,
  busy = false,
  onClose,
  onConfirm,
}: ConfirmationDialogProps) {
  const isDecision = intent !== 'default'

  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose()
    }
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, busy, onClose])

  if (!open) return null

  const busyLabel = busy ? 'Υποβολή…' : confirmLabel

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/55 p-4 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirmation-dialog-title"
      aria-describedby={
        description ? 'confirmation-dialog-description' : undefined
      }
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose()
      }}
    >
      <div
        className={cx(
          'w-full max-w-lg rounded-3xl border p-6 shadow-2xl',
          isDecision
            ? intentPanelClass[intent]
            : 'border-zinc-200 bg-white text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {isDecision ? (
          <>
            <div className="flex items-start gap-3">
              <IntentBadge intent={intent} />
              <div className="min-w-0">
                <h2 id="confirmation-dialog-title" className="text-lg font-bold">
                  {title}
                </h2>
                {description ? (
                  <p
                    id="confirmation-dialog-description"
                    className="mt-2 text-sm leading-6 opacity-85"
                  >
                    {description}
                  </p>
                ) : null}
              </div>
            </div>

            {summary ? (
              <div className="mt-5 rounded-2xl bg-white/70 p-4 text-sm text-zinc-800 ring-1 ring-black/5 dark:bg-zinc-950/45 dark:text-zinc-100 dark:ring-white/10">
                <p className="font-semibold">{summary.title}</p>
                {summary.lines?.map((line, i) => (
                  <p key={i} className="mt-1 text-zinc-600 dark:text-zinc-300">
                    {line}
                  </p>
                ))}
              </div>
            ) : null}

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={onClose}
                fullWidth
              >
                {cancelLabel}
              </Button>
              <Button
                type="button"
                disabled={busy}
                onClick={onConfirm}
                fullWidth
                className={cx(
                  'text-white shadow-md disabled:opacity-60',
                  intentConfirmClass[intent],
                )}
              >
                {busyLabel}
              </Button>
            </div>
          </>
        ) : (
          <>
            <h2
              id="confirmation-dialog-title"
              className="text-lg font-bold text-zinc-900 dark:text-white"
            >
              {title}
            </h2>
            {description ? (
              <p
                id="confirmation-dialog-description"
                className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400"
              >
                {description}
              </p>
            ) : null}
            {summary ? (
              <div className="mt-4 rounded-2xl border border-zinc-200 bg-zinc-50 p-4 text-sm dark:border-zinc-700 dark:bg-zinc-950/50">
                <p className="font-semibold text-zinc-900 dark:text-white">
                  {summary.title}
                </p>
                {summary.lines?.map((line, i) => (
                  <p key={i} className="mt-1 text-zinc-600 dark:text-zinc-300">
                    {line}
                  </p>
                ))}
              </div>
            ) : null}
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={onClose}
                fullWidth
              >
                {cancelLabel}
              </Button>
              <Button
                type="button"
                variant="solid"
                disabled={busy}
                onClick={onConfirm}
                fullWidth
              >
                {busyLabel}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
